import React, { useContext } from "react";

import { Flex, Link, Text } from "flicket-ui";
import { FormikHelpers } from "formik";

import { Footer, Seo, Menu } from "~components";
import { BaseContext } from "~context";
import { useIsMobile } from "~hooks";
import { handlePromise, sdk } from "~lib";

import {
  AuthContainer,
  ContentContainer,
  MobileLogo,
  ForgotPasswordForm,
} from "~components/auth";
import { Layout } from "~components/common/Layout";
import { ForgotPasswordValues } from "~form/forgotPassword";
import { RequestResetPasswordInput } from "~graphql/sdk";
import { useReCaptchaSDK } from "~hooks/useReCaptcha";
import { getError, showToast } from "~lib/helpers";

const handleForgotPassword = async (
  sdkFn: typeof sdk,
  orgId: string,
  data: RequestResetPasswordInput
) =>
  handlePromise(async () =>
    sdkFn({ orgId }).requestResetPassword({ input: data })
  );

const ForgotPassword = () => {
  const isMobile = useIsMobile();
  const { organization } = useContext(BaseContext);
  const { recaptchaSdkGenerator } = useReCaptchaSDK();

  const handleSubmit = async (
    values: ForgotPasswordValues,
    actions: FormikHelpers<ForgotPasswordValues>
  ) => {
    const { error } = await handleForgotPassword(
      await recaptchaSdkGenerator("resetPassword"),
      organization?.id,
      values
    );

    actions.setSubmitting(false);

    if (error) {
      return showToast(getError(error, "graphQL"), "error");
    }

    return showToast("An email has been sent");
  };

  return (
    <>
      <Seo
        title="Forgot password"
        description={`Reset your password for ${organization?.name}`}
      />
      <Menu />
      <Layout layoutType="auth">
        <ContentContainer>
          <MobileLogo logo={organization?.branding?.logo} />
          <Flex
            bg="N100"
            justifyContent="flex-start"
            flex={1}
            alignItems="center"
            flexDir="column"
          >
            <AuthContainer>
              <Flex flexDir="column" flex={1}>
                <Text
                  fontSize={6}
                  fontWeight="heavy"
                  color="N800"
                  textAlign="center"
                >
                  Forgot password
                </Text>
                <ForgotPasswordForm handleSubmit={handleSubmit} />
              </Flex>
            </AuthContainer>
            <Flex
              justifyContent="center"
              alignItems="center"
              mt={2}
              mb={{ _: 3, md: "28px" as any }}
            >
              <Text
                fontSize={1}
                lineHeight="high"
                fontWeight="demiBold"
                mr="1/2"
              >
                No Account yet?
              </Text>
              <Link
                to="/signup"
                color="P300"
                textDecoration="underline"
                fontWeight="heavy"
                fontSize={1}
              >
                Create account
              </Link>
            </Flex>
          </Flex>
          {!isMobile && <Footer />}
        </ContentContainer>
      </Layout>
    </>
  );
};

export default ForgotPassword;
